/**
 * @file SlideOutButton.js
 * @version 2.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-01-30
 * @modified 2026-10-15
 *
 * 2.3.0 (2026-10-15, Cam — "some view modals are not calling it"): buttons that
 * appear AFTER the hook ran are measured too. A view window's buttons usually
 * arrive with its data (loading → loaded), a status-dependent button appears
 * later, a modal opened over a screen renders through a portal a cycle after
 * its parent's effect — and every one of those sat at the collapsed 45px unless
 * its caller happened to pass the right deps. While any component using the
 * hook is mounted, one shared MutationObserver on document.body measures each
 * .btn-slideout as it is added (and the row it sits in), and one shared
 * ResizeObserver follows every label. Each caller's own deps-driven measure is
 * kept exactly as it was.
 */

import { useLayoutEffect } from 'react';

const ROW_SELECTOR = '.buttons.slideout, .header-actions';

// The collapsed button is 45px and expands to --expanded-width on hover, so the
// width has to be measured from the rendered label.
function measure(button) {
  const label = button.querySelector('.btn-label');
  if (!label) return;
  const badge = button.querySelector('.btn-badge');
  const expandedWidth = 45 + label.offsetWidth + (badge ? 24 : 0) + 12;
  const next = `${expandedWidth}px`;
  // Only write when it actually moved: a no-op style write on every observer
  // callback would be a lot of churn for nothing.
  if (button.style.getPropertyValue('--expanded-width') !== next) {
    button.style.setProperty('--expanded-width', next);
  }
}

// Reserve the fully-expanded width of a whole row of buttons on its container,
// so a header's columns are laid out once and do not move when a button opens.
// Without this the buttons' grid track grows on hover and steals width from
// `.workflow-info` beside it, which reflows and re-wraps its text every time the
// pointer crosses a button — the page appears to twitch. Only header rows
// consume the variable (see index.css); setting it everywhere costs nothing and
// keeps the measurement in one place.
function reserveRowWidth(container) {
  const children = [...container.children];
  if (children.length === 0) return;
  const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
  const total = children.reduce((sum, child) => {
    const button = child.matches('.btn-slideout') ? child : child.querySelector('.btn-slideout');
    // A non-slide-out child (a plain icon button, a divider) contributes the
    // width it already has.
    const width = button
      ? parseFloat(button.style.getPropertyValue('--expanded-width')) || button.offsetWidth
      : child.offsetWidth;
    return sum + width;
  }, 0) + gap * (children.length - 1);
  const next = `${Math.ceil(total)}px`;
  if (container.style.getPropertyValue('--slideout-row-width') !== next) {
    container.style.setProperty('--slideout-row-width', next);
  }
}

// ── Shared observers ─────────────────────────────────────────────────────────
// One of each for the whole page, alive while at least one caller is mounted.

let shared = null; // { users, resize, mutation, observed: WeakSet<label> }

function onResize(entries) {
  const rows = new Set();
  for (const entry of entries) {
    const button = entry.target.closest('.btn-slideout');
    if (!button) continue;
    measure(button);
    const row = button.closest(ROW_SELECTOR);
    if (row) rows.add(row);
  }
  rows.forEach(reserveRowWidth);
}

// Measure a button now and follow its label from here on: whatever changes
// the text ("Save" → "Saving…", a count), the measurement follows.
function track(button) {
  measure(button);
  if (!shared || !shared.resize) return;
  const label = button.querySelector('.btn-label');
  if (label && !shared.observed.has(label)) {
    shared.observed.add(label);
    shared.resize.observe(label);
  }
}

function forget(node) {
  if (!shared || !shared.resize || node.nodeType !== 1) return;
  const labels = node.matches('.btn-label') ? [node] : node.querySelectorAll('.btn-label');
  labels.forEach((label) => {
    if (shared.observed.has(label)) {
      shared.observed.delete(label);
      shared.resize.unobserve(label);
    }
  });
}

function elementOf(node) {
  if (!node) return null;
  return node.nodeType === 1 ? node : node.parentElement;
}

// A button added anywhere — a modal's footer after its data loaded, a portal
// rendered a cycle late, a label swapped inside an existing button — is
// measured as it lands, before the browser paints it.
function onMutations(records) {
  const buttons = new Set();
  const rows = new Set();
  for (const record of records) {
    const target = elementOf(record.target);
    if (target) {
      const inButton = target.closest('.btn-slideout');
      if (inButton) buttons.add(inButton);
      // A button added to or removed from a row changes the row's width.
      const row = target.matches(ROW_SELECTOR) ? target : target.closest(ROW_SELECTOR);
      if (row) rows.add(row);
    }
    record.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches('.btn-slideout')) buttons.add(node);
      node.querySelectorAll('.btn-slideout').forEach((b) => buttons.add(b));
    });
    record.removedNodes.forEach(forget);
  }
  buttons.forEach((button) => {
    if (!button.isConnected) return;
    track(button);
    const row = button.closest(ROW_SELECTOR);
    if (row) rows.add(row);
  });
  rows.forEach((row) => { if (row.isConnected) reserveRowWidth(row); });
}

function acquire() {
  if (shared) {
    shared.users += 1;
    return;
  }
  shared = {
    users: 1,
    resize: typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize),
    mutation: null,
    observed: new WeakSet()
  };
  if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined' && document.body) {
    shared.mutation = new MutationObserver(onMutations);
    shared.mutation.observe(document.body, { childList: true, subtree: true });
  }
}

function release() {
  if (!shared) return;
  shared.users -= 1;
  if (shared.users > 0) return;
  if (shared.mutation) shared.mutation.disconnect();
  if (shared.resize) shared.resize.disconnect();
  shared = null;
}

/**
 * Size every slide-out button on screen to its own label.
 *
 * Measuring once per `deps` change was fragile: a label carrying a dynamic
 * part — "Save changes (3)", "Move 12", "Saving..." — changes width without the
 * deps necessarily changing, and a button that appears after the effect ran
 * (data loaded, a status changed, a portal rendered late) was never measured
 * at all and stayed a 45px circle on hover. A ResizeObserver on each label and
 * a MutationObserver on the page (both shared by every caller) close those off.
 * `deps` still re-measures everything on screen when they change, as before.
 * .btn-label is white-space: nowrap with no width of its own, so its box is
 * always the natural text width and observing it is safe — the button's width
 * never feeds back into it, so there is no loop.
 */
export function useSlideOutButton(deps = []) {
  // The shared observers live while this component is mounted.
  useLayoutEffect(() => {
    acquire();
    return release;
  }, []);

  useLayoutEffect(() => {
    const buttons = document.querySelectorAll('.btn-slideout');
    buttons.forEach(track);

    const rows = new Set();
    buttons.forEach((button) => {
      const row = button.closest(ROW_SELECTOR);
      if (row) rows.add(row);
    });
    rows.forEach(reserveRowWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// For tests: how many callers hold the shared observers (0 when none).
export function slideOutObserverUsers() {
  return shared ? shared.users : 0;
}
